import { ListFilter } from "lucide-react";
import { forwardRef, type ReactNode } from "react";
import { IconButton, type IconButtonProps } from "@/components/ui/icon-button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

type ModuleFilterTriggerProps = Omit<
  IconButtonProps,
  "aria-label" | "children"
> & {
  active?: boolean;
  activeCount?: number;
  /** Shown next to the icon while active (e.g. "Waiting"). */
  activeLabel?: string;
  ariaLabel?: string;
};

/**
 * Filter control for the end of {@link ModuleSearchField}: a quiet icon at
 * rest; while filtering, a soft blue tag with the filter's name (or the
 * number of filters) so it is clear what is applied. No box of its own.
 * Use as a Popover or DropdownMenu trigger (`asChild`).
 */
export const ModuleFilterTrigger = forwardRef<
  HTMLButtonElement,
  ModuleFilterTriggerProps
>(function ModuleFilterTrigger(
  {
    active = false,
    activeCount,
    activeLabel,
    ariaLabel = "Filters",
    className,
    ...rest
  },
  ref,
) {
  const count = activeCount ?? (active ? 1 : 0);
  const tag = activeLabel?.trim() || (count > 1 ? String(count) : null);
  return (
    <IconButton
      ref={ref}
      type="button"
      {...rest}
      aria-label={
        count > 0
          ? `${ariaLabel}, ${activeLabel?.trim() || `${count} active`}`
          : ariaLabel
      }
      variant="ghost"
      className={cn(
        active
          ? "size-auto h-6 max-w-32 gap-1 rounded-md bg-primary/10 px-1.5 text-primary hover:bg-primary/15 hover:text-primary"
          : "size-7 text-muted-foreground hover:text-foreground",
        className,
      )}
    >
      <ListFilter className="size-3.5 shrink-0" />
      {active && tag ? (
        <span className="truncate text-xs font-medium">{tag}</span>
      ) : null}
    </IconButton>
  );
});

/**
 * Filter icon + panel. Put as `trailing` on {@link ModuleSearchField}.
 */
export const ModuleFilterPopover = ({
  active = false,
  activeCount,
  ariaLabel = "Filters",
  children,
  contentClassName,
  align = "end",
}: ModuleFilterTriggerProps & {
  children: ReactNode;
  contentClassName?: string;
  align?: "start" | "center" | "end";
}) => (
  <Popover>
    <PopoverTrigger asChild>
      <ModuleFilterTrigger
        active={active}
        activeCount={activeCount}
        ariaLabel={ariaLabel}
      />
    </PopoverTrigger>
    <PopoverContent
      align={align}
      className={cn("w-72 space-y-4", contentClassName)}
      onInteractOutside={(event) => {
        const target = event.target as HTMLElement | null;
        if (target?.closest("[data-slot='select-content']")) {
          event.preventDefault();
        }
      }}
    >
      {children}
    </PopoverContent>
  </Popover>
);
